import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchListingDetail,
  fetchListingComparables,
  type ComparableRow,
  type ComparablesResponse,
  type ListingDetailDTO,
  type RentVerdictBucket
} from '$lib/api/listingDetail';

// Operator-facing detail page for a single Listing. The schema-foundation
// PR #98 added `address_precision` / `confidence_score`; this page is the
// first place those values surface to the operator. The decision (PR #101)
// to gate listing→Unit linkage off means the `unit` block is empty for
// every listing today — the section still renders but tells the operator
// "no BBR profile linked yet" so they understand what they're seeing.

export function ListingDetailPage() {
  const { listingId } = useParams<{ listingId: string }>();
  const query = useQuery({
    queryKey: ['listing-detail', listingId],
    queryFn: () => fetchListingDetail(listingId ?? ''),
    enabled: !!listingId
  });
  const comparables = useQuery({
    queryKey: ['listing-comparables', listingId],
    queryFn: () => fetchListingComparables(listingId ?? '', 10),
    enabled: !!listingId
  });

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      <header className="flex shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-4 py-3">
        <Link to="/units" className="text-xs text-fg-muted hover:text-fg">
          ← Listings
        </Link>
        <h1 className="text-md font-semibold text-fg" data-testid="listing-detail-title">
          Listing detail
        </h1>
      </header>

      <section className="flex-1 p-4">
        {query.isPending && (
          <div className="text-xs text-fg-muted">Loading…</div>
        )}
        {query.isError && (
          <div className="text-xs text-red-600 dark:text-red-400" role="alert">
            Failed to load listing.
          </div>
        )}
        {query.data === null && (
          <div className="text-xs text-fg-muted">Listing not found.</div>
        )}
        {query.data && (
          <DetailBody
            data={query.data}
            comparables={comparables.data ?? null}
            comparablesLoading={comparables.isPending}
          />
        )}
      </section>
    </div>
  );
}

interface BodyProps {
  data: ListingDetailDTO;
  comparables: ComparablesResponse | null;
  comparablesLoading: boolean;
}

function DetailBody({ data, comparables, comparablesLoading }: BodyProps) {
  const latest = data.snapshots[0];
  const olderSnapshots = useMemo(() => data.snapshots.slice(1), [data.snapshots]);
  const score = data.confidence_score;
  const scoreColor =
    score >= 75 ? 'text-green-700 dark:text-green-400'
    : score >= 50 ? 'text-amber-700 dark:text-amber-400'
    : 'text-red-700 dark:text-red-400';

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="rounded border border-rule bg-surface p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="text-md font-semibold text-fg">
              {latest?.title ?? '(no title)'}
            </div>
            <div className="mt-1 text-sm text-fg-muted">
              {latest?.address_raw ?? '—'}
              {(latest?.postal_code || latest?.city) && (
                <>
                  {' · '}
                  {latest?.postal_code} {latest?.city}
                </>
              )}
            </div>
          </div>
          <a
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded border border-rule px-2 py-1 text-xs text-fg hover:bg-surface-2"
            data-testid="listing-detail-portal-link"
          >
            Open on {data.portal_name ?? data.portal_slug ?? 'portal'} ↗
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Section title="Lifecycle">
          <Row label="status" value={data.status} />
          <Row label="in_scope" value={String(data.in_scope)} />
          <Row label="first_seen_at" value={fmtDate(data.first_seen_at)} />
          <Row label="last_seen_at" value={fmtDate(data.last_seen_at)} />
          <Row label="portal" value={data.portal_name ?? data.portal_slug ?? '—'} />
        </Section>

        <Section title="Conviction">
          <Row
            label="confidence_score"
            value={
              <span className={`font-mono font-semibold ${scoreColor}`}>
                {score}/100
              </span>
            }
          />
          <Row label="address_precision" value={data.address_precision} />
          <Row label="floor_source" value={data.floor_source} />
          <Row label="comparables_eligible" value={String(data.comparables_eligible)} />
          <Row label="address_confidence" value={data.address_confidence ?? '—'} />
        </Section>

        <Section title="Pricing (latest)">
          <Row label="rent" value={fmtKr(latest?.monthly_rent_dkk ?? null)} />
          <Row label="deposit" value={fmtKr(latest?.deposit_dkk ?? null)} />
          <Row label="prepaid" value={fmtKr(latest?.prepaid_rent_dkk ?? null)} />
          <Row label="aconto" value={fmtKr(latest?.aconto_dkk ?? null)} />
        </Section>

        <Section title="Property (latest)">
          <Row label="size_m2" value={latest?.area_m2 ?? '—'} />
          <Row label="rooms" value={latest?.rooms ?? '—'} />
          <Row label="floor" value={latest?.floor ?? '—'} />
          <Row label="property_type" value={latest?.property_type ?? '—'} />
          <Row label="energy_label" value={latest?.energy_label ?? '—'} />
          <Row
            label="lat / lng"
            value={
              latest?.latitude != null && latest?.longitude != null
                ? `${latest.latitude.toFixed(4)}, ${latest.longitude.toFixed(4)}`
                : '—'
            }
          />
        </Section>
      </div>

      <Section title="Linked BBR Unit">
        {data.unit ? (
          <>
            <Row label="bbr_unit_id" value={data.unit.bbr_unit_id ?? '—'} />
            <Row label="address" value={data.unit.address ?? '—'} />
            <Row label="floor / door" value={`${data.unit.floor ?? '—'} / ${data.unit.door ?? '—'}`} />
            <Row label="area_m2" value={data.unit.area_m2 ?? '—'} />
            <Row label="construction_year" value={data.unit.construction_year ?? '—'} />
          </>
        ) : (
          <div className="text-xs text-fg-muted">
            No BBR unit linked. Listing→Unit linkage is gated off until the
            DAWA <code>/adresser</code> work lands and <code>Unit.floor</code>/
            <code>Unit.door</code> are populated. Listing fields are still
            authoritative for comparables.
          </div>
        )}
      </Section>

      {latest?.description && (
        <Section title="Description (latest)">
          <div className="whitespace-pre-wrap text-xs text-fg-muted">
            {latest.description}
          </div>
        </Section>
      )}

      <Section title="Comparables (top 10)">
        {comparablesLoading && (
          <div className="text-xs text-fg-muted">Loading comparables…</div>
        )}
        {!comparablesLoading && comparables && (
          <ComparablesBlock data={comparables} />
        )}
        {!comparablesLoading && !comparables && (
          <div className="text-xs text-fg-muted">No comparables available.</div>
        )}
      </Section>

      {olderSnapshots.length > 0 && (
        <Section title={`Snapshot history (${data.snapshots.length} total)`}>
          <table className="w-full border-collapse text-xs" data-testid="listing-snapshot-history">
            <thead>
              <tr className="border-b border-rule text-left text-2xs uppercase tracking-wide text-fg-muted">
                <th className="px-2 py-1">scraped_at</th>
                <th className="px-2 py-1">rent</th>
                <th className="px-2 py-1">deposit</th>
                <th className="px-2 py-1">size</th>
                <th className="px-2 py-1">rooms</th>
                <th className="px-2 py-1">floor</th>
              </tr>
            </thead>
            <tbody>
              {data.snapshots.map((s) => (
                <tr key={s.id} className="border-b border-rule">
                  <td className="px-2 py-1 font-mono text-fg-muted">{fmtDate(s.scraped_at)}</td>
                  <td className="px-2 py-1 font-mono text-fg">{fmtKr(s.monthly_rent_dkk)}</td>
                  <td className="px-2 py-1 font-mono text-fg">{fmtKr(s.deposit_dkk)}</td>
                  <td className="px-2 py-1 font-mono text-fg">{s.area_m2 ?? '—'}</td>
                  <td className="px-2 py-1 font-mono text-fg">{s.rooms ?? '—'}</td>
                  <td className="px-2 py-1 font-mono text-fg">{s.floor ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-rule bg-surface p-3">
      <h3 className="mb-2 text-2xs uppercase tracking-wide text-fg-muted">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-xs">
      <span className="w-32 shrink-0 font-mono text-fg-muted">{label}</span>
      <span className="font-mono text-fg">{value}</span>
    </div>
  );
}

interface ComparablesBlockProps {
  data: ComparablesResponse;
}

function ComparablesBlock({ data }: ComparablesBlockProps) {
  const verdict = data.verdict;
  const items = data.items;
  return (
    <div className="space-y-3">
      <VerdictStrip verdict={verdict} />
      {items.length === 0 ? (
        <div className="text-xs text-fg-muted">
          No similar listings found. Try a wider scope or wait for more data.
        </div>
      ) : (
        <table className="w-full border-collapse text-xs" data-testid="listing-comparables-table">
          <thead>
            <tr className="border-b border-rule text-left text-2xs uppercase tracking-wide text-fg-muted">
              <th className="px-2 py-1">address</th>
              <th className="px-2 py-1">size</th>
              <th className="px-2 py-1">rooms</th>
              <th className="px-2 py-1">floor</th>
              <th className="px-2 py-1">rent</th>
              <th className="px-2 py-1">kr/m²</th>
              <th className="px-2 py-1">distance</th>
              <th className="px-2 py-1">match</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <ComparableRowDisplay key={c.listing_id} row={c} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const VERDICT_COPY: Record<RentVerdictBucket, { label: string; tone: string }> = {
  very_cheap: { label: 'very cheap', tone: 'text-green-700 dark:text-green-400 bg-green-500/10' },
  cheap:      { label: 'cheap',      tone: 'text-green-700 dark:text-green-400 bg-green-500/10' },
  fair:       { label: 'fair',       tone: 'text-fg bg-surface-2' },
  expensive:  { label: 'expensive',  tone: 'text-amber-700 dark:text-amber-400 bg-amber-500/10' },
  overpriced: { label: 'overpriced', tone: 'text-red-700 dark:text-red-400 bg-red-500/10' },
  unknown:    { label: 'too few comparables', tone: 'text-fg-muted bg-surface-2' }
};

function VerdictStrip({ verdict }: { verdict: ComparablesResponse['verdict'] }) {
  const copy = VERDICT_COPY[verdict.bucket];
  return (
    <div className={`flex flex-wrap items-baseline gap-3 rounded px-3 py-2 text-xs ${copy.tone}`} data-testid="listing-verdict">
      <span className="font-semibold uppercase tracking-wide">{copy.label}</span>
      <span className="font-mono">
        n=<span className="font-semibold">{verdict.n}</span>
      </span>
      {verdict.target_price_per_sqm != null && (
        <span className="font-mono">
          this: <span className="font-semibold">{verdict.target_price_per_sqm.toFixed(0)}</span> kr/m²
        </span>
      )}
      {verdict.median_price_per_sqm != null && (
        <span className="font-mono">
          median: <span className="font-semibold">{verdict.median_price_per_sqm.toFixed(0)}</span> kr/m²
        </span>
      )}
      {verdict.diff_pct != null && (
        <span className="font-mono">
          diff: <span className="font-semibold">
            {verdict.diff_pct > 0 ? '+' : ''}{verdict.diff_pct.toFixed(1)}%
          </span>
        </span>
      )}
      {verdict.p25_price_per_sqm != null && verdict.p75_price_per_sqm != null && (
        <span className="font-mono text-fg-muted">
          p25–p75: {verdict.p25_price_per_sqm.toFixed(0)}–{verdict.p75_price_per_sqm.toFixed(0)}
        </span>
      )}
    </div>
  );
}

function ComparableRowDisplay({ row }: { row: ComparableRow }) {
  const dist =
    row.distance_m == null
      ? '—'
      : row.distance_m < 1000
      ? `${Math.round(row.distance_m)}m`
      : `${(row.distance_m / 1000).toFixed(1)}km`;
  const linkTarget = `/listings/${row.listing_id}`;
  return (
    <tr className="border-b border-rule">
      <td className="px-2 py-1 text-fg">
        <Link to={linkTarget} className="hover:text-accent">
          <div className="truncate max-w-[16rem]" title={row.address ?? ''}>
            {row.address ?? '—'}
          </div>
          <div className="text-2xs text-fg-muted">
            {row.postal_code} {row.city} · {row.portal_slug}
          </div>
        </Link>
      </td>
      <td className="px-2 py-1 font-mono text-fg">{row.area_m2 ?? '—'}m²</td>
      <td className="px-2 py-1 font-mono text-fg">{row.rooms ?? '—'}</td>
      <td className="px-2 py-1 font-mono text-fg">{row.floor ?? '—'}</td>
      <td className="px-2 py-1 font-mono text-fg">{fmtKr(row.monthly_rent_dkk)}</td>
      <td className="px-2 py-1 font-mono text-fg">
        {row.price_per_sqm != null ? row.price_per_sqm.toFixed(0) : '—'}
      </td>
      <td className="px-2 py-1 font-mono text-fg-muted">{dist}</td>
      <td className="px-2 py-1 font-mono text-fg-muted">
        {(row.similarity_score * 100).toFixed(0)}%
      </td>
      <td className="px-2 py-1">
        {row.url && (
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-2xs text-fg-muted hover:text-accent"
          >
            ↗
          </a>
        )}
      </td>
    </tr>
  );
}

function fmtKr(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v.toLocaleString('da-DK')} kr`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('da-DK', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
}
